import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteQuestionnaire } from '../../hooks/useDeleteQuestionnaire';
import { useQuestionnaireDetails } from '../../hooks/useQuestionnaireDetails';
import { useQuestionnaires } from '../../hooks/useQuestionnaires';

import {
  Button,
  Container,
  ContentContainerQuestionnaire,
  ContentWrapper,
  DetailLabel,
  DetailSection,
  DetailTitle,
  DetailValue,
  HeaderActions,
  Question,
  QuestionList,
  QuestionnaireDescription,
  QuestionnaireDetail,
  QuestionnaireItem,
  QuestionnaireList,
  QuestionnaireTitle,
  QuestionNumber,
  QuestionText,
  Title,
} from './Questionnaires.Styles';

import styled from '@emotion/styled/macro';

/**
 * Array of colors used for the left border of questionnaire items
 * @type {Array<string>}
 */
const borderColors = ['#3b78e7', '#ff8c42', '#00b894', '#e17055', '#6c5ce7'];

/**
 * Styled button component for destructive actions like delete
 * @component
 * @extends Button
 */
const DangerButton = styled(Button)`
  background-color: #e74c3c;

  &:hover {
    background-color: #c0392b;
  }
`;

/**
 * Container for grouping action buttons
 * @component
 */
const ActionsGroup = styled.div`
  display: flex;
  gap: 10px;
`;

/**
 * @module QuestionnairesPage
 * @description Component for managing questionnaires in the Mental Health Portal application.
 * Displays a list of questionnaires, shows details of the selected questionnaire,
 * and provides options to create, edit, and delete questionnaires.
 *
 * @requires react
 * @requires react-i18next
 * @requires react-router-dom
 * @requires @emotion/styled/macro
 * @requires ../../hooks/useDeleteQuestionnaire
 * @requires ../../hooks/useQuestionnaireDetails
 * @requires ../../hooks/useQuestionnaires
 * @requires ./Questionnaires.Styles
 */
export default function QuestionnairesPage() {
  /** Translation hook for internationalization */
  const { t } = useTranslation();
  /** Navigation hook for routing */
  const navigate = useNavigate();
  /**
   * @state {number|null} activeId - ID of the currently selected questionnaire
   */
  const [activeId, setActiveId] = useState(null);

  /**
   * Custom hook for fetching all questionnaires
   * @type {Object}
   * @property {Array<Object>} questionnaires - List of all questionnaires
   * @property {boolean} loading - Loading state for questionnaires list
   * @property {string|null} error - Error message if fetch fails
   */
  const { questionnaires, loading, error } = useQuestionnaires();

  /**
   * Custom hook for fetching details of the selected questionnaire
   * @type {Object}
   * @property {Object} questionnaire - The selected questionnaire's detailed data
   * @property {boolean} loadingDetails - Loading state for questionnaire details
   * @property {string|null} errorDetails - Error message if fetch fails
   */
  const {
    questionnaire,
    loading: loadingDetails,
    error: errorDetails,
  } = useQuestionnaireDetails(activeId);

  /**
   * Custom hook for deleting questionnaires
   * @type {Object}
   * @property {Function} deleteQuestionnaire - Function to delete a questionnaire
   * @property {boolean} deleting - Loading state during deletion
   */
  const { deleteQuestionnaire, loading: deleting } = useDeleteQuestionnaire();

  /**
   * Effect hook to automatically select the first questionnaire when the list loads
   * @effect
   * @param {Array<Object>} questionnaires - The list of questionnaires
   * @param {number|null} activeId - Currently selected questionnaire ID
   * @returns {void}
   */
  useEffect(() => {
    if (questionnaires.length > 0 && !activeId) {
      setActiveId(questionnaires[0].id);
    }
  }, [questionnaires, activeId]);

  /**
   * Navigates to the create questionnaire page
   * @function handleNewQuestionnaire
   * @returns {void}
   */
  const handleNewQuestionnaire = () => {
    navigate('/createQuestionnaire');
  };

  /**
   * Navigates to the edit questionnaire page for the selected questionnaire
   * @function handleEditQuestionnaire
   * @returns {void}
   */
  const handleEditQuestionnaire = () => {
    if (questionnaire) {
      navigate(`/editQuestionnaire/${questionnaire.id}`);
    }
  };

  /**
   * Deletes the selected questionnaire after confirmation
   * @function handleDeleteQuestionnaire
   * @async
   * @returns {Promise<void>}
   */
  const handleDeleteQuestionnaire = async () => {
    if (!questionnaire) return;

    // Ask for confirmation before deleting
    const confirmed = window.confirm(
      `${t('Are you sure you want to delete the questionnaire')} "${questionnaire.name}"?`
    );
    if (!confirmed) return;

    // Delete the questionnaire and refresh the page
    await deleteQuestionnaire(questionnaire.id);
    window.location.reload();
  };

  /**
   * Render loading state while fetching questionnaires
   */
  if (loading) return <div>{t('Loading questionnaires')}</div>;

  /**
   * Render error state if fetching questionnaires fails
   */
  if (error)
    return (
      <div>
        {t('Error')}: {error}
      </div>
    );

  return (
    <Container>
      <Title>{t('Questionnaires')}</Title>

      <ContentWrapper>
        <HeaderActions>
          <Button onClick={handleNewQuestionnaire}>
            {t('New Questionnaire')}
          </Button>

          <ActionsGroup>
            <Button onClick={handleEditQuestionnaire} disabled={!questionnaire}>
              {t('Edit')}
            </Button>

            <DangerButton
              onClick={handleDeleteQuestionnaire}
              disabled={!questionnaire || deleting}
            >
              {deleting ? `${t('Deleting')}` : `${t('Delete')}`}
            </DangerButton>
          </ActionsGroup>
        </HeaderActions>

        <ContentContainerQuestionnaire>
          <QuestionnaireList>
            {questionnaires.map((q, index) => (
              <QuestionnaireItem
                key={q.id}
                borderColor={borderColors[index % borderColors.length]}
                onClick={() => setActiveId(q.id)}
              >
                <QuestionnaireTitle>{q.name}</QuestionnaireTitle>
                <QuestionnaireDescription>
                  {q.description || q.introduction}
                </QuestionnaireDescription>
              </QuestionnaireItem>
            ))}
          </QuestionnaireList>

          {loadingDetails && <div>{t('Loading details')}</div>}
          {errorDetails && (
            <div>
              {t('Error loading details')} {errorDetails}
            </div>
          )}

          {questionnaire && (
            <QuestionnaireDetail>
              <DetailTitle>{questionnaire.name}</DetailTitle>

              <DetailSection>
                <DetailLabel>{t('When to send')}</DetailLabel>
                <DetailValue>{questionnaire.to_be_applied_at}</DetailValue>
              </DetailSection>

              <DetailSection>
                <DetailLabel>{t('Instructions')}</DetailLabel>
                <DetailValue>{questionnaire.introduction}</DetailValue>
              </DetailSection>

              <QuestionList>
                {questionnaire.questions?.map((q, index) => (
                  <Question key={index}>
                    <QuestionNumber>{index + 1}.</QuestionNumber>
                    <QuestionText>{q.question}</QuestionText>
                  </Question>
                ))}
              </QuestionList>
            </QuestionnaireDetail>
          )}
        </ContentContainerQuestionnaire>
      </ContentWrapper>
    </Container>
  );
}
